import React, { useEffect, useState } from 'react';
import { Fields, WarningLevel } from '../core/_models';
import { KTSVG } from '../../../../_metronic/helpers';
import type { LibraryApi } from '../core/libraryApi';
import SearchableSelect from '../../../shared/components/SearchableSelect';
import { toast } from 'react-toastify';
import { getApiErrorMessage } from '../../../shared/utils/apiErrorMessage';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    fieldData?: Fields | null;
    onFieldEdited: () => void;
    api: LibraryApi;
}

export type { Props as EditFieldModalProps };

const EditFieldModal: React.FC<Props> = ({ onClose, isOpen, fieldData, onFieldEdited, api }) => {
    const [labelName, setLabelName] = useState("");
    const [inputType, setInputType] = useState("text");
    const [warningOnly, setWarningOnly] = useState<WarningLevel | null>(null);
    const [minValue, setMinValue] = useState<number | undefined>();
    const [maxValue, setMaxValue] = useState<number | undefined>();
    const [unit, setUnit] = useState("");
    const [options, setOptions] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [optionInput, setOptionInput] = useState("");
    const [isRequired, setIsRequired] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [editableIndex, setEditableIndex] = useState<number | null>(null);
    const [active, setActive] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        if (fieldData) {
            setLabelName(fieldData.label);
            setInputType(fieldData.fieldType);
            setMinValue(fieldData.minValue);
            setMaxValue(fieldData.maxValue);
            setUnit(fieldData.unit || "");
            setOptions(fieldData?.optionsJson ?? []);
            setWarningOnly(fieldData.warningOnly ?? null);
            setActive(fieldData.isActive);
            setIsRequired(fieldData.required);
            setIsReadOnly(fieldData.readOnly);
        }
    }, [fieldData]);

    const handleEditFieldItem = async () => {
        if (!labelName.trim()) {
            toast.warning('Please enter a field label');
            return;
        }

        if (minValue !== undefined && maxValue !== undefined && minValue > maxValue) {
            toast.warning('Min value cannot be greater than Max value');
            return;
        }

        if ((inputType === 'select' || inputType === 'multiselect') && options.length === 0) {
            toast.warning('Please add at least one option for select field');
            return;
        }

        try {
            if (!fieldData || !fieldData.id || !fieldData.submenuId) {
                console.error('Missing required data to update field');
                toast.error('Missing required data to update field');
                return;
            }

            await api.updateField(
                fieldData.submenuId, // submenu ID
                fieldData.id,         // field ID
                labelName,
                inputType,
                isRequired,
                isReadOnly,
                warningOnly ?? undefined,
                active ?? undefined,
                unit || undefined,
                minValue,
                maxValue,
                options.length > 0 ? options : undefined,
                fieldData.order
            );

            toast.success('Field updated successfully');
            onFieldEdited();
            onClose();
        } catch (err) {
            console.error('Failed to update field:', err);
            toast.error(getApiErrorMessage(err, 'Failed to update field'));
        }
    };

    const handleAddOption = () => {
        if (optionInput.trim()) {
            setOptions(prev => [...prev, optionInput.trim()]);
            setOptionInput("");
        }
    };

    const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = Number(e.target.value);
        setMinValue(value);

        if (maxValue !== undefined && value > maxValue) {
            setError("Min value cannot be greater than max value.");
        } else {
            setError(null);
        }
    };

    const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = Number(e.target.value);
        setMaxValue(value);

        if (minValue !== undefined && value < minValue) {
            setError("Max value cannot be less than min value.");
        } else {
            setError(null);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="custom-modal-header d-flex justify-content-between align-items-center">
                    <h5 className="m-0">Edit Field</h5>
                    <button className="close-btn" onClick={onClose}>
                        <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
                    </button>
                </div>

                <div className="custom-modal-body">
                    <div style={{ paddingInline: '2.5rem', marginBottom: '1rem' }}>
                        <div className='d-flex align-items-center mb-5'>
                            <label className="modal_label me-3 mb-0">
                                Not editable
                            </label>
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="requiredCheckbox"
                                checked={isReadOnly}
                                onChange={(e) => setIsReadOnly(e.target.checked)}
                            />
                        </div>
                        {!isReadOnly ?
                            <div>
                                <label className='modal_label'>Label Name</label>
                                <input
                                    type='text'
                                    value={labelName}
                                    onChange={(e) => setLabelName(e.target.value)}
                                    className='form-control'
                                />
                            </div> :
                            <div>
                                <label className='modal_label'>Label Name</label>
                                <SearchableSelect
                                    className='form-select py-4'
                                    value={labelName}
                                    onChange={(e) => setLabelName(e.target.value)}
                                >
                                    <option value="">Select Label</option>
                                    <option value="Vessel Name">Vessel Name</option>
                                    <option value="Voyage Number">Voyage Number</option>
                                    <option value="Report Type">Report Type</option>
                                    <option value="Departure Port">Departure Port</option>
                                    <option value="Departure Datetime">Departure Datetime</option>
                                    <option value="Arrival Port">Arrival Port</option>
                                    <option value="ETA Datetime">ETA Datetime</option>
                                </SearchableSelect>
                            </div>}
                        <label className='modal_label mt-5'>Choose Input Type:</label>
                        <SearchableSelect
                            className='form-select py-4'
                            value={inputType}
                            onChange={(e) => {
                                setInputType(e.target.value);
                                setOptions([]); // reset options if type changes
                            }}
                        >
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                            <option value="datetime">Date Time</option>
                            <option value="checkbox">Checkbox</option>
                            <option value="radio">Radio Buttons</option>
                            <option value="select">Dropdown (Select)</option>
                            <option value="textarea">Textarea</option>
                        </SearchableSelect>
                        {(inputType === "number" || inputType === "text") && (
                            <div className="mt-4">
                                <label className="modal_label">Validation Range</label>
                                <div className="d-flex gap-3">
                                    <input
                                        type="number"
                                        value={minValue ?? ""}
                                        onChange={handleMinChange}
                                        className="form-control no-spinner"
                                        placeholder="Min Value"
                                    />
                                    <input
                                        type="number"
                                        value={maxValue ?? ""}
                                        onChange={handleMaxChange}
                                        className="form-control no-spinner"
                                        placeholder="Max Value"
                                    />
                                </div>
                                {error && (
                                    <div className="text-danger mt-2">
                                        <small>{error}</small>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Show options input only if needed */}
                        {(inputType === "select" || inputType === "radio" || inputType === "checkbox") && (
                            <div className="mt-4">
                                <label className='modal_label'>Add Options:</label>
                                <div className="d-flex gap-2">
                                    <input
                                        type="text"
                                        value={optionInput}
                                        onChange={(e) => setOptionInput(e.target.value)}
                                        className="form-control"
                                        placeholder="Option value"
                                    />
                                    <button className="btn btn-outline-secondary" onClick={handleAddOption}>Add</button>
                                </div>
                                <ul className="mt-2 ps-0">
                                    {options.map((opt, idx) => (
                                        <li
                                            key={idx}
                                            className='list-group-item mb-2 d-flex align-items-center justify-content-between'
                                            style={{ color: '#1f2937', fontWeight: 500 }}
                                        >
                                            <input
                                                type="text"
                                                className="form-control me-2"
                                                value={opt}
                                                disabled={editableIndex !== idx}
                                                onChange={(e) => {
                                                    const updatedOptions = [...options];
                                                    updatedOptions[idx] = e.target.value;
                                                    setOptions(updatedOptions);
                                                }}
                                            />

                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-sm px-0"
                                                    onClick={() =>
                                                        editableIndex === idx
                                                            ? setEditableIndex(null)
                                                            : setEditableIndex(idx)
                                                    }
                                                >
                                                    {editableIndex === idx ? <KTSVG path='/media/map/save-1.svg' className='svg-icon-2' /> : <KTSVG path='/media/map/edit-active.svg' className='' />
                                                    }
                                                </button>
                                                <button
                                                    className="btn btn-sm px-0"
                                                    onClick={() => {
                                                        const updatedOptions = options.filter((_, i) => i !== idx);
                                                        setOptions(updatedOptions);
                                                        if (editableIndex === idx) setEditableIndex(null);
                                                    }}
                                                >
                                                    <KTSVG path='/media/map/trash.svg' className='' />
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {(inputType === "number" || inputType === "text") && (
                            <>
                                <label className='modal_label mt-5'>Unit</label>
                                <SearchableSelect
                                    className='form-select py-4'
                                    value={unit}
                                    onChange={(e) => setUnit(e.target.value)}
                                >
                                    <option value="">Select a unit</option>
                                    <option value="%">Percentage (%)</option>
                                    <option value="Hrs">Hrs</option>
                                    <option value="Deg">Deg</option>
                                    <option value="min">min</option>
                                    <option value="rev">rev</option>
                                    <option value="kWh">kWh</option>
                                    <option value="kW">kW</option>
                                    <option value="RPM">RPM</option>
                                    <option value="Bar">Bar</option>
                                    <option value="m">m</option>
                                    <option value="m³">m³</option>
                                    <option value="MT">MT</option>
                                    <option value="NM">NM</option>
                                    <option value="C">Celsius (°C)</option>
                                    <option value="mbar">mbar (Millibar)</option>
                                    <option value="bar">bar (Bar)</option>
                                    <option value="psi">psi (Pounds per square inch)</option>
                                    <option value="Pa">Pa (Pascal)</option>
                                    <option value="kPa">kPa (Kilopascal)</option>
                                    <option value="atm">atm (Atmosphere)</option>
                                    <option value="mmHg">mmHg (Millimeters of mercury)</option>
                                    <option value="inHg">inHg (Inches of mercury)</option>
                                    <option value="°C">°C (Celsius)</option>
                                    <option value="knots">knots</option>
                                    <option value="m/s">m/s (Meters per second)</option>
                                    <option value="ppt">ppt (Salinity)</option>
                                    <option value="sec">sec</option>
                                </SearchableSelect>
                            </>
                        )}
                        {
                            inputType === "number" && (
                                <>
                                    <label className='modal_label mt-5'>Validation Type</label>
                                    <div className='d-flex gap-4 mb-5'>
                                        <div className="form-check">
                                            <input
                                                type='radio'
                                                value={WarningLevel.STRICT}
                                                name='type'
                                                className="form-check-input"
                                                checked={warningOnly === WarningLevel.STRICT}
                                                onChange={() => setWarningOnly(WarningLevel.STRICT)}
                                            />
                                            <label className="modal_label">Strict</label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                type='radio'
                                                value={WarningLevel.SOFT}
                                                name='type'
                                                className="form-check-input"
                                                checked={warningOnly === WarningLevel.SOFT}
                                                onChange={() => setWarningOnly(WarningLevel.SOFT)}
                                            />
                                            <label className="modal_label">Soft</label>
                                        </div>
                                    </div>
                                </>
                            )
                        }
                        {!isReadOnly && <div className='d-flex align-items-center mt-5'>
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="requiredCheckbox"
                                checked={isRequired}
                                onChange={(e) => setIsRequired(e.target.checked)}
                            />
                            <label className="modal_label ps-3 mb-0">
                                Required
                            </label>
                        </div>}
                    </div>
                </div>

                <div className="d-flex justify-content-end gap-2">
                    <button className="btn btn_secondary" onClick={onClose}>Cancel</button>
                    <button className="btn btn_primary" onClick={handleEditFieldItem}>Update</button>
                </div>
            </div>
        </div>
    );
};

export default EditFieldModal;
